import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DwgSvgViewer.css';
import DOMPurify from 'dompurify';

/* ── helpers ─────────────────────────────────── */

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }

/** Parse SVG text and extract distinct `<g>` layer names from inkscape:label, id, etc. */
function extractSvgLayers(svgText) {
  if (!svgText) return [];
  const layers = [];
  // Match <g> elements with an id or inkscape:label that looks like a layer name
  const re = /<g[^>]*?\bid=["']([^"']+)["'][^>]*>/gi;
  let m;
  while ((m = re.exec(svgText)) !== null) {
    const id = m[1];
    // Ignore ezdxf internal scaffolding ids
    if (id && !id.startsWith('__') && id.length < 100) {
      layers.push({ id, label: id, visible: true });
    }
  }
  return layers;
}

/** Distance in px between two pointer positions */
function pointerDist(a, b) {
  return Math.sqrt((a.clientX - b.clientX) ** 2 + (a.clientY - b.clientY) ** 2);
}

/* ── SVG Icons (inline, no emoji) ────────────── */
function IcZoomIn(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>; }
function IcZoomOut(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>; }
function IcFitView(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>; }
function IcLayers(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>; }
function IcRuler(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21.3 15.3a2.4 2.4 0 010 3.4l-2.6 2.6a2.4 2.4 0 01-3.4 0L2.7 8.7a2.4 2.4 0 010-3.4l2.6-2.6a2.4 2.4 0 013.4 0z"/><path d="M14.5 12.5l2-2"/><path d="M11.5 9.5l2-2"/><path d="M8.5 6.5l2-2"/><path d="M17.5 15.5l2-2"/></svg>; }
function IcMove(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>; }
function IcReset(p) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>; }

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 20;
const ZOOM_STEP = 1.2;

/**
 * Interactive SVG viewer for DWG/DXF files (rendered as SVG by cad-converter).
 *
 * Features: pan, zoom (scroll + pinch + buttons), fit-to-view, layer toggle,
 * measurement mode (pixel-based distance), keyboard shortcuts.
 */
export default function DwgSvgViewer({ svgUrl, svgText: svgTextProp, filename, onError, className }) {
  const containerRef = useRef(null);
  const svgWrapRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null);
  const pinchRef = useRef(null);
  const [layers, setLayers] = useState([]);
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [measureMode, setMeasureMode] = useState(false);
  const [measureStart, setMeasureStart] = useState(null);
  const [measureEnd, setMeasureEnd] = useState(null);
  const [measureLive, setMeasureLive] = useState(null);
  const fitPendingRef = useRef(false);

  /* ── load SVG ── */
  useEffect(() => {
    if (svgTextProp) {
      setSvgContent(svgTextProp);
      setLayers(extractSvgLayers(svgTextProp));
      setError('');
      fitPendingRef.current = true;
      return;
    }
    if (!svgUrl) { setSvgContent(''); setLayers([]); return; }

    let cancelled = false;
    setLoading(true);
    setError('');
    setSvgContent('');
    setLayers([]);

    (async () => {
      try {
        const tenantSlug = (() => {
          try { return String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); }
          catch { return ''; }
        })();
        const res = await fetch(svgUrl, {
          credentials: 'include',
          headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {},
        });
        if (!res.ok) {
          let detail = '';
          try {
            const body = await res.json();
            detail = body?.detail || body?.hint || '';
          } catch {
            detail = await res.text().catch(() => '');
          }
          if (res.status === 503) {
            throw new Error('CAD-konverteraren är inte tillgänglig. DWG-förhandsgranskning kräver att konverteringstjänsten körs.');
          }
          throw new Error(detail || `HTTP ${res.status}`);
        }
        const text = await res.text();
        if (cancelled) return;
        setSvgContent(text);
        setLayers(extractSvgLayers(text));
        fitPendingRef.current = true;
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || 'Kunde inte ladda SVG-förhandsvisning';
        setError(msg);
        onError?.(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [svgUrl, svgTextProp, onError]);

  /* ── fit to view ── */
  const fitToView = useCallback(() => {
    const container = containerRef.current;
    const svgWrap = svgWrapRef.current;
    if (!container || !svgWrap) return;

    const svgEl = svgWrap.querySelector('svg');
    if (!svgEl) return;

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (!cw || !ch) return;

    // Get the SVG's intrinsic size from viewBox or width/height attributes
    let sw, sh;
    const vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const parts = vb.split(/[\s,]+/).map(Number);
      if (parts.length >= 4) { sw = parts[2]; sh = parts[3]; }
    }
    if (!sw) sw = svgEl.width?.baseVal?.value || svgEl.clientWidth || cw;
    if (!sh) sh = svgEl.height?.baseVal?.value || svgEl.clientHeight || ch;

    const scaleX = (cw - 32) / sw; // 16px padding each side
    const scaleY = (ch - 32) / sh;
    const newZoom = clamp(Math.min(scaleX, scaleY), MIN_ZOOM, MAX_ZOOM);

    setZoom(newZoom);
    setPan({
      x: (cw - sw * newZoom) / 2,
      y: (ch - sh * newZoom) / 2,
    });
  }, []);

  /* Auto-fit after SVG loads */
  useEffect(() => {
    if (!svgContent || !fitPendingRef.current) return;
    // Wait one frame for DOM to render
    const raf = requestAnimationFrame(() => {
      fitToView();
      fitPendingRef.current = false;
    });
    return () => cancelAnimationFrame(raf);
  }, [svgContent, fitToView]);

  /* ── zoom via scroll ── */
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    setZoom((prev) => {
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const next = clamp(prev * factor, MIN_ZOOM, MAX_ZOOM);
      const ratio = next / prev;
      setPan((p) => ({
        x: cx - ratio * (cx - p.x),
        y: cy - ratio * (cy - p.y),
      }));
      return next;
    });
  }, []);

  /* ── pan via drag ── */
  const handlePointerDown = useCallback((e) => {
    if (measureMode) return; // measurement mode handles clicks separately
    if (e.button !== 0 && e.button !== 1) return; // left or middle click
    e.preventDefault();
    setDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: 0, panY: 0 };
    e.currentTarget?.setPointerCapture?.(e.pointerId);
  }, [measureMode]);

  const handlePointerMove = useCallback((e) => {
    if (!dragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    dragStartRef.current.panX = dx;
    dragStartRef.current.panY = dy;
    setPan((p) => ({
      x: p.x + (dx - (dragStartRef.current._lastDx || 0)),
      y: p.y + (dy - (dragStartRef.current._lastDy || 0)),
    }));
    dragStartRef.current._lastDx = dx;
    dragStartRef.current._lastDy = dy;
  }, [dragging]);

  const handlePointerUp = useCallback((e) => {
    if (!dragging) return;
    setDragging(false);
    dragStartRef.current = null;
    e.currentTarget?.releasePointerCapture?.(e.pointerId);
  }, [dragging]);

  /* ── touch pinch-to-zoom ── */
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        dist: pointerDist(e.touches[0], e.touches[1]),
        zoom,
      };
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const newDist = pointerDist(e.touches[0], e.touches[1]);
      const scale = newDist / pinchRef.current.dist;
      setZoom(clamp(pinchRef.current.zoom * scale, MIN_ZOOM, MAX_ZOOM));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    pinchRef.current = null;
  }, []);

  /* ── measurement mode clicks ── */
  const handleMeasureClick = useCallback((e) => {
    if (!measureMode) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const point = {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
    if (!measureStart) {
      setMeasureStart(point);
      setMeasureEnd(null);
      setMeasureLive(null);
    } else {
      setMeasureEnd(point);
      setMeasureLive(null);
    }
  }, [measureMode, measureStart, pan, zoom]);

  const handleMeasureMove = useCallback((e) => {
    if (!measureMode || !measureStart || measureEnd) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setMeasureLive({
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    });
  }, [measureMode, measureStart, measureEnd, pan, zoom]);

  const measuredPx = useMemo(() => {
    const end = measureEnd || measureLive;
    if (!measureStart || !end) return null;
    return Math.sqrt((end.x - measureStart.x) ** 2 + (end.y - measureStart.y) ** 2);
  }, [measureStart, measureEnd, measureLive]);

  /* ── layer toggle ── */
  const toggleLayer = useCallback((id) => {
    setLayers((prev) => prev.map((l) => l.id === id ? { ...l, visible: !l.visible } : l));
  }, []);

  /* Apply layer visibility to DOM */
  useEffect(() => {
    const wrap = svgWrapRef.current;
    if (!wrap) return;
    for (const layer of layers) {
      const el = wrap.querySelector(`#${CSS.escape(layer.id)}`);
      if (el) el.style.display = layer.visible ? '' : 'none';
    }
  }, [layers]);

  /* ── keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e) => {
      const key = (e.key || '').toLowerCase();
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (key === '+' || key === '=') { e.preventDefault(); setZoom((z) => clamp(z * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM)); }
      if (key === '-') { e.preventDefault(); setZoom((z) => clamp(z / ZOOM_STEP, MIN_ZOOM, MAX_ZOOM)); }
      if (key === 'f') { e.preventDefault(); fitToView(); }
      if (key === '0') { e.preventDefault(); setZoom(1); setPan({ x: 0, y: 0 }); }
      if (key === 'l') { e.preventDefault(); setLayerPanelOpen((s) => !s); }
      if (key === 'm') { e.preventDefault(); setMeasureMode((s) => !s); setMeasureStart(null); setMeasureEnd(null); setMeasureLive(null); }
      if (key === 'escape') {
        if (measureMode) { setMeasureMode(false); setMeasureStart(null); setMeasureEnd(null); setMeasureLive(null); }
        if (layerPanelOpen) setLayerPanelOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fitToView, measureMode, layerPanelOpen]);

  /* ── Passive: false wheel listener ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const opts = { passive: false };
    el.addEventListener('wheel', handleWheel, opts);
    return () => el.removeEventListener('wheel', handleWheel, opts);
  }, [handleWheel]);

  /* ── render ── */
  const zoomPct = `${Math.round(zoom * 100)}%`;
  const cursorStyle = measureMode ? 'crosshair' : (dragging ? 'grabbing' : 'grab');
  const svgTransform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;

  const measEnd = measureEnd || measureLive;

  return (
    <div className={`dwg-svg-viewer${className ? ` ${className}` : ''}`}>
      {/* Toolbar */}
      <div className="dwg-toolbar">
        <div className="dwg-toolbar__group">
          <button className="dwg-toolbar__btn" title="Zooma in (+)" onClick={() => setZoom((z) => clamp(z * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM))}><IcZoomIn /></button>
          <button className="dwg-toolbar__btn" title="Zooma ut (-)" onClick={() => setZoom((z) => clamp(z / ZOOM_STEP, MIN_ZOOM, MAX_ZOOM))}><IcZoomOut /></button>
          <div className="dwg-toolbar__zoom-pct">{zoomPct}</div>
          <button className="dwg-toolbar__btn" title="Anpassa (F)" onClick={fitToView}><IcFitView /></button>
          <button className="dwg-toolbar__btn" title="Nollställ vy (0)" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}><IcReset /></button>
        </div>
        <div className="dwg-toolbar__sep" />
        <div className="dwg-toolbar__group">
          <button className={`dwg-toolbar__btn${measureMode ? ' dwg-toolbar__btn--active' : ''}`} title="Mät avstånd (M)" onClick={() => { setMeasureMode((s) => !s); setMeasureStart(null); setMeasureEnd(null); setMeasureLive(null); }}><IcRuler /></button>
          {layers.length > 0 && (
            <button className={`dwg-toolbar__btn${layerPanelOpen ? ' dwg-toolbar__btn--active' : ''}`} title="Lager (L)" onClick={() => setLayerPanelOpen((s) => !s)}><IcLayers /></button>
          )}
        </div>
        {filename && (
          <>
            <div className="dwg-toolbar__sep" />
            <div className="dwg-toolbar__filename" title={filename}>{filename}</div>
          </>
        )}
        <div className="dwg-toolbar__spacer" />
        {measureMode && (
          <div className="dwg-toolbar__mode-badge">
            <IcMove style={{ width: 14, height: 14 }} /> Mätläge
          </div>
        )}
      </div>

      {/* Canvas area */}
      <div
        className="dwg-canvas"
        ref={containerRef}
        style={{ cursor: cursorStyle }}
        onPointerDown={handlePointerDown}
        onPointerMove={(e) => { handlePointerMove(e); handleMeasureMove(e); }}
        onPointerUp={handlePointerUp}
        onClick={handleMeasureClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {loading && (
          <div className="dwg-canvas__loading">
            <span className="dwg-spinner" />
            Laddar ritning...
          </div>
        )}

        {error && !loading && (
          <div className="dwg-canvas__error">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            <div className="dwg-canvas__error-text">{error}</div>
            <div className="dwg-canvas__error-hint">Kontrollera att CAD-konverteraren är aktiv och att filen är en giltig DWG/DXF.</div>
          </div>
        )}

        {svgContent && !loading && (
          <div
            ref={svgWrapRef}
            className="dwg-canvas__svg-wrap"
            style={{ transform: svgTransform, transformOrigin: '0 0' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svgContent, { USE_PROFILES: { svg: true, svgFilters: true }, ADD_TAGS: ['use'], FORBID_TAGS: ['script', 'foreignObject'], FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover'] }) }}
          />
        )}

        {!svgContent && !loading && !error && (
          <div className="dwg-canvas__empty">
            <IcMove style={{ width: 40, height: 40, opacity: 0.3 }} />
            <div>Ingen ritning laddad</div>
          </div>
        )}

        {/* Measurement overlay */}
        {measureMode && measureStart && measEnd && (
          <svg className="dwg-canvas__measure-overlay" style={{ pointerEvents: 'none' }}>
            <line
              x1={measureStart.x * zoom + pan.x}
              y1={measureStart.y * zoom + pan.y}
              x2={measEnd.x * zoom + pan.x}
              y2={measEnd.y * zoom + pan.y}
              stroke="var(--dwg-measure-line, #2563EB)"
              strokeWidth="2"
              strokeDasharray="6 3"
            />
            <circle cx={measureStart.x * zoom + pan.x} cy={measureStart.y * zoom + pan.y} r="4" fill="var(--dwg-measure-line, #2563EB)" />
            <circle cx={measEnd.x * zoom + pan.x} cy={measEnd.y * zoom + pan.y} r="4" fill="var(--dwg-measure-line, #2563EB)" />
            {measuredPx != null && (
              <text
                x={(measureStart.x * zoom + pan.x + measEnd.x * zoom + pan.x) / 2}
                y={(measureStart.y * zoom + pan.y + measEnd.y * zoom + pan.y) / 2 - 10}
                textAnchor="middle"
                fill="var(--dwg-measure-line, #2563EB)"
                fontSize="13"
                fontWeight="600"
                fontFamily="Inter, system-ui, sans-serif"
              >
                {Math.round(measuredPx)} px
              </text>
            )}
          </svg>
        )}
      </div>

      {/* Layer panel */}
      {layerPanelOpen && layers.length > 0 && (
        <div className="dwg-layers">
          <div className="dwg-layers__header">
            <IcLayers style={{ width: 15, height: 15 }} />
            <span>Lager ({layers.length})</span>
            <button className="dwg-layers__close" onClick={() => setLayerPanelOpen(false)} aria-label="Stäng lagerpanel">
              <svg viewBox="0 0 15 15" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3.5 3.5l8 8M11.5 3.5l-8 8"/></svg>
            </button>
          </div>
          <div className="dwg-layers__list">
            {layers.map((l) => (
              <label key={l.id} className="dwg-layers__item">
                <input
                  type="checkbox"
                  checked={l.visible}
                  onChange={() => toggleLayer(l.id)}
                />
                <span className="dwg-layers__label" title={l.label}>{l.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Status bar */}
      <div className="dwg-statusbar">
        <span className="dwg-statusbar__item">
          <span className={`dwg-statusbar__dot${svgContent ? ' dwg-statusbar__dot--ok' : (error ? ' dwg-statusbar__dot--err' : '')}`} />
          {svgContent ? 'Redo' : (loading ? 'Laddar' : (error ? 'Fel' : 'Ingen ritning'))}
        </span>
        <span className="dwg-statusbar__item">{zoomPct}</span>
        <span className="dwg-statusbar__spacer" />
        <span className="dwg-statusbar__item dwg-statusbar__shortcuts">
          <kbd>F</kbd> Anpassa
          <kbd>+/-</kbd> Zoom
          <kbd>M</kbd> Mät
          {layers.length > 0 && <><kbd>L</kbd> Lager</>}
        </span>
      </div>
    </div>
  );
}
